/**
 * Reusable Activity + Notes panel for any recipient (contact, company).
 *
 * Mirrors ProjectDetailActivity but reads from recipientActivitySlice; the
 * timeline, filter tabs and notes column are the same components.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingSpinner, Button } from 'components/common';
import { LuPlus } from 'react-icons/lu';
import {
  getRecipientActivities, getRecipientTasks,
  createRecipientActivity, updateRecipientActivity, deleteRecipientActivity,
  createRecipientTask,     updateRecipientTask,     deleteRecipientTask,
  createRecipientNote,     updateRecipientNote, deleteRecipientNote,
  getRecipientNotes,
  resetRecipientActivity,
} from 'redux/crm/recipientActivitySlice';
import ActivityForm       from 'components/app/crm/project/activity/ActivityForm';
import TaskForm           from 'components/app/crm/project/activity/TaskForm';
import ActivityTimeline   from 'components/app/crm/project/activity/ActivityTimeline';
import ActivityFilterTabs from 'components/app/crm/project/activity/ActivityFilterTabs';
import NotesPanel         from 'components/app/crm/project/activity/NotesPanel';
import { ActivityEmptyState } from 'components/app/crm/project/activity/ActivityEmptyStates';

const submittingSelector = s => s.crm_recipient_activity.submitting;

const RecipientActivityPanel = ({ recipientType, recipientId }) => {
  const dispatch = useDispatch();
  const { activities, tasks, notes } = useSelector(s => s.crm_recipient_activity);

  const [filter,        setFilter]        = useState('all');
  const [showActivity,  setShowActivity]  = useState(false);
  const [showTask,      setShowTask]      = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  // Reset when the recipient changes
  useEffect(() => {
    dispatch(resetRecipientActivity());
    setFilter('all');
  }, [recipientType, recipientId, dispatch]);

  useEffect(() => {
    if (!recipientId || filter === 'task') return;
    dispatch(getRecipientActivities({
      recipientType, recipientId, page: 1,
      activityType: filter !== 'all' ? filter : undefined,
      activeOnly:   false,
    }));
  }, [recipientType, recipientId, filter, dispatch]);

  useEffect(() => {
    if (!recipientId) return;
    if (filter !== 'all' && filter !== 'task') return;
    dispatch(getRecipientTasks({ recipientType, recipientId, page: 1, activeOnly: false }));
  }, [recipientType, recipientId, filter, dispatch]);

  // Row actions must hit the recipient endpoints, not the project ones the
  // timeline defaults to.
  const rowActions = useMemo(() => ({
    updateActivity: (args) => updateRecipientActivity({ recipientType, recipientId, ...args }),
    deleteActivity: (args) => deleteRecipientActivity({ recipientType, recipientId, ...args }),
    updateTask:     (args) => updateRecipientTask({ recipientType, recipientId, ...args }),
    deleteTask:     (args) => deleteRecipientTask({ recipientType, recipientId, ...args }),
  }), [recipientType, recipientId]);

  const fetchNotes = useCallback(
    ({ page, q }) => dispatch(getRecipientNotes({ recipientType, recipientId, page, q })),
    [dispatch, recipientType, recipientId],
  );

  const allItems = useMemo(() => {
    const acts = (activities.data || []).map(a => ({ ...a, _type: 'activity' }));
    const tks  = (tasks.data      || []).map(t => ({ ...t, _type: 'task' }));
    if (filter === 'task') return tks;
    if (filter !== 'all')  return acts;
    return [...acts, ...tks];
  }, [activities.data, tasks.data, filter]);

  const listLoading    = activities.loading || tasks.loading;
  const initialLoading = listLoading && allItems.length === 0;

  const handleEdit = useCallback((item) => {
    setEditingRecord(item);
    if (item._type === 'task') setShowTask(true);
    else setShowActivity(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setEditingRecord(null);
    setShowActivity(false);
    setShowTask(false);
  }, []);

  const openCreateActivity = useCallback(() => { setEditingRecord(null); setShowActivity(true); }, []);
  const openCreateTask     = useCallback(() => { setEditingRecord(null); setShowTask(true); }, []);

  return (
    <div className="tw-flex tw-flex-col xl:tw-flex-row tw-gap-6 tw-min-h-[420px]">
      {/* ── Left: activity feed ── */}
      <div className="tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-gap-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-flex-wrap">
          <h6 className="tw-text-base tw-font-bold tw-text-slate-800 tw-mb-0">Activity</h6>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Button variant="primary" size="sm" leftIcon={LuPlus} onClick={openCreateActivity}>
              Customer interaction
            </Button>
            <Button variant="secondary" size="sm" leftIcon={LuPlus} onClick={openCreateTask}>
              Task
            </Button>
          </div>
        </div>

        <div className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm tw-overflow-hidden">
          <div className="tw-px-3 tw-py-2.5 tw-border-b tw-border-slate-100">
            <ActivityFilterTabs value={filter} onChange={setFilter} />
          </div>

          {initialLoading ? (
            <div className="tw-flex tw-justify-center tw-py-12"><LoadingSpinner /></div>
          ) : allItems.length === 0 ? (
            <ActivityEmptyState
              onCreateActivity={openCreateActivity}
              onCreateTask={openCreateTask}
              filtered={filter !== 'all'}
            />
          ) : (
            <ActivityTimeline
              items={allItems}
              onEdit={handleEdit}
              projectId={recipientId}
              loading={listLoading}
              actions={rowActions}
            />
          )}
        </div>
      </div>

      {/* ── Right: notes ── */}
      <NotesPanel
        recipientId={recipientId}
        notes={notes}
        onFetch={fetchNotes}
        showPagination={false}
        createAction={(args) => createRecipientNote({ recipientType, recipientId, ...args })}
        updateAction={(args) => updateRecipientNote({ recipientType, recipientId, ...args })}
        deleteAction={(args) => deleteRecipientNote({ recipientType, recipientId, ...args })}
        submittingSelector={submittingSelector}
      />

      {showActivity && (
        <ActivityForm
          open={showActivity}
          onClose={handleCloseForm}
          projectId={recipientId}
          record={editingRecord?._type === 'activity' ? editingRecord : null}
          createAction={(args) => createRecipientActivity({ recipientType, recipientId, ...args })}
          updateAction={(args) => updateRecipientActivity({ recipientType, recipientId, ...args })}
          submittingSelector={submittingSelector}
        />
      )}
      {showTask && (
        <TaskForm
          open={showTask}
          onClose={handleCloseForm}
          projectId={recipientId}
          record={editingRecord?._type === 'task' ? editingRecord : null}
          createAction={(args) => createRecipientTask({ recipientType, recipientId, ...args })}
          updateAction={(args) => updateRecipientTask({ recipientType, recipientId, ...args })}
          submittingSelector={submittingSelector}
        />
      )}
    </div>
  );
};

RecipientActivityPanel.propTypes = {
  recipientType: PropTypes.oneOf(['contact', 'company']).isRequired,
  recipientId:   PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default RecipientActivityPanel;
